import moment from "moment-timezone";
import Decimal from "decimal.js";
import { Badge } from "@/components/ui/badge";
import type {
  ExpandedTransformedEntry,
  ExpandedTransformedSettlement,
  ExpandedTransformedAdvance,
  ExpandedGrade,
  ExpandedChargeGradeDetail,
  TransformedSplit,
} from "@/lib/api/agtech/ticketsExpanded";

// ── Helpers ────────────────────────────────────────────────────────

export function fmt(val: string | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return val;
}

export function fmtNum(val: string | null | undefined, conversion_factor?: string | null): string {
  if (val === null || val === undefined) return "—";

  return new Decimal(val).div(conversion_factor ?? 1).toNumber().toLocaleString()
}

export function fmtWeight(val: string | null | undefined, uom = " lbs"): string {
  if (val === null || val === undefined) return "—";
  return Number(val).toLocaleString() + uom;
}

// ── Field ──────────────────────────────────────────────────────────

export function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      <span className="text-xs leading-tight">{value}</span>
    </div>
  );
}

export function NumericField({
  label,
  value,
  uom,
  conversion_factor,
}: {
  label: string;
  value: string | null | undefined;
  uom: string;
  conversion_factor?: string | null;
}) {
  const formatted =
    value === null || value === undefined
      ? null
      : new Decimal(value).div(conversion_factor ?? 1).toNumber().toLocaleString();

  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      {formatted === null ? (
        <span className="text-xs leading-tight">—</span>
      ) : (
        <span className="text-xs leading-tight">
          <span className="font-medium">{formatted}</span>
          <span className="text-[10px] text-muted-foreground ml-0.5">{uom}</span>
        </span>
      )}
    </div>
  );
}

// ── Grades sub-block ───────────────────────────────────────────────

export function GradesBlock({ grades }: { grades: ExpandedGrade[] }) {
  if (grades.length === 0) return null;

  return (
    <div className="mt-1">
      <span className="text-[10px] font-medium text-muted-foreground">Grades</span>
      <div className="grid grid-cols-4 gap-x-3 gap-y-0.5 mt-0.5">
        {grades.map((g, i) => (
          <div key={`${g.charge_grade_code}-${i}`} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{g.charge_grade_code}</span>
            <span className="font-medium">{fmt(g.charge_grade_value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Base grades sub-block (origin / destination) ───────────────────

export function BaseGradesBlock({ grades }: { grades: ExpandedChargeGradeDetail[] }) {
  if (grades.length === 0) return null;

  return (
    <div className="mt-1">
      <span className="text-[10px] font-medium text-muted-foreground">Grades</span>
      <div className="grid grid-cols-4 gap-x-3 gap-y-0.5 mt-0.5">
        {grades.filter((g) => g.charge_grade_value_o !== '0').map((g, i) => (
          <div key={`${g.charge_grade_code}-${i}`} className="text-xs">
            <span className="text-muted-foreground">{g.charge_grade_code}:</span>
            <span className="ml-1 font-medium">{fmt(g.charge_grade_value_o)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Cash formula line ──────────────────────────────────────────────

function CashFormulaLine({
  label,
  cashApplied,
  cashCharges,
  cashAmount,
}: {
  label: string;
  cashApplied: string | null;
  cashCharges: string | null;
  cashAmount: string | null;
}) {
  if (cashAmount === null && cashApplied === null) return null;

  const appliedFmt = cashApplied !== null ? new Decimal(cashApplied).toNumber().toLocaleString() : null;
  const chargesFmt = cashCharges !== null ? new Decimal(cashCharges).toNumber().toLocaleString() : null;
  const amountFmt = cashAmount !== null ? new Decimal(cashAmount).toNumber().toLocaleString() : null;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium text-muted-foreground leading-tight">{label}</span>
      <div className="flex items-center gap-1 text-xs flex-wrap">
        {appliedFmt !== null && (
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">${appliedFmt}</span>
        )}
        {chargesFmt !== null && (
          <>
            <span className="text-muted-foreground">−</span>
            <span className="text-red-500 dark:text-red-400 font-medium">${chargesFmt}</span>
          </>
        )}
        {amountFmt !== null && (
          <>
            <span className="text-muted-foreground">=</span>
            <span className="font-semibold">${amountFmt}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Settlement block ───────────────────────────────────────────────

export function SettlementBlock({ s, e, onClick }: { s: ExpandedTransformedSettlement; e: ExpandedTransformedEntry; onClick?: () => void }) {
  const showShipFrom = s.ShipFrom?.long_name !== e.ShipFrom?.long_name;
  const showShipTo = s.ShipTo?.long_name !== e.ShipTo?.long_name;
  const hasPricing = s.price !== null || s.net_price !== null || s.discounts_price !== null;
  const hasCash = s.cash_applied !== null || s.cash_settlement_amount !== null;

  return (
    <div className={`rounded border bg-background p-2 space-y-1.5${onClick ? " cursor-pointer hover:bg-muted/50 transition-colors" : ""}`} onClick={onClick}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Badge variant="default" className="text-[10px] px-1.5 py-0">Settlement</Badge>
        <span className="text-xs font-semibold">#{(s.serial_nbr !== null ? String(s.serial_nbr) : undefined) ?? "-"}</span>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1.5">
        {/* Left: contract & quantity info */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 min-w-0">
          <Field label="Contract" value={String(s.contract?.contract_id ?? "-")} />
          <Field label="Contract Type" value={String(s.contract?.pricing_type_code ?? "-")} />
          {s.contract?.quantity && (<NumericField label="Contract Qty" value={String(s.contract?.quantity ?? "")} uom={""} />)}
          <NumericField label="Share" value={s.vp_quantity} uom={e.dest_uom} />
          <NumericField label="Applied" value={s.vp_applied_quantity} uom={e.dest_uom} />
          {showShipFrom && (<Field label="Ship From" value={s.ShipFrom?.long_name ?? fmt(s.ship_from_id)} />)}
          {showShipTo && (<Field label="Ship To" value={s.ShipTo?.long_name ?? fmt(s.ship_to_id)} />)}
        </div>

        {/* Right: pricing & cash */}
        {(hasPricing || hasCash) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 min-w-0 border-l pl-4 border-border">
            {s.price !== null && (<NumericField label="Price" value={s.price} uom="" />)}
            {s.discounts_price !== null && (<NumericField label="Discounts" value={s.discounts_price} uom="" />)}
            {s.net_price !== null && (<NumericField label="Net Price" value={s.net_price} uom="" />)}
            {s.cash_charges !== null && (<NumericField label="Charges" value={s.cash_charges} uom="" />)}
            {s.charges_details !== null && (<Field label="Charges Detail" value={s.charges_details} />)}
            <CashFormulaLine
              label="Cash Settlement"
              cashApplied={s.cash_applied}
              cashCharges={s.cash_charges}
              cashAmount={s.cash_settlement_amount}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Advance block ──────────────────────────────────────────────────

export function AdvanceBlock({ a, e, onClick }: { a: ExpandedTransformedAdvance; e: ExpandedTransformedEntry; onClick?: () => void }) {
  const showShipFrom = a.ShipFrom?.long_name !== e.ShipFrom?.long_name;
  const showShipTo = a.ShipTo?.long_name !== e.ShipTo?.long_name;
  const hasPricing = a.price !== null || a.net_price !== null || a.discounts_price !== null;
  const hasCash = a.cash_applied !== null || a.cash_advance_amount !== null;

  return (
    <div className={`rounded border bg-background p-2 space-y-1.5${onClick ? " cursor-pointer hover:bg-muted/50 transition-colors" : ""}`} onClick={onClick}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Advance</Badge>
        <span className="text-xs font-semibold">#{(a.serial_nbr !== null ? String(a.serial_nbr) : undefined) ?? "-"}</span>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1.5">
        {/* Left: contract & quantity info */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 min-w-0">
          <Field label="Contract" value={String(a.contract?.contract_id ?? "-")} />
          <Field label="Contract Type" value={String(a.contract?.pricing_type_code ?? "-")} />
          <NumericField label="Share" value={a.vp_quantity} uom={e.dest_uom} />
          <NumericField label="Applied" value={a.vp_applied_quantity} uom={e.dest_uom} />
          {showShipFrom && (<Field label="Ship From" value={a.ShipFrom?.long_name ?? fmt(a.ship_from_id)} />)}
          {showShipTo && (<Field label="Ship To" value={a.ShipTo?.long_name ?? fmt(a.ship_to_id)} />)}
        </div>

        {/* Right: pricing & cash */}
        {(hasPricing || hasCash) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 min-w-0 border-l pl-4 border-border">
            {a.price !== null && (<NumericField label="Price" value={a.price} uom="" />)}
            {a.discounts_price !== null && (<NumericField label="Discounts" value={a.discounts_price} uom="" />)}
            {a.net_price !== null && (<NumericField label="Net Price" value={a.net_price} uom="" />)}
            {a.cash_charges !== null && (<NumericField label="Charges" value={a.cash_charges} uom="" />)}
            {a.charges_details !== null && (<Field label="Charges Detail" value={a.charges_details} />)}
            <CashFormulaLine
              label="Cash Advance"
              cashApplied={a.cash_applied}
              cashCharges={a.cash_charges}
              cashAmount={a.cash_advance_amount}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Splits block ───────────────────────────────────────────────────

export function SplitsBlock({ splits, e }: { splits: TransformedSplit[]; e: ExpandedTransformedEntry }) {
  if (splits.length === 0) return null;

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-1 gap-1">
        {splits.map((sp, i) => (
          <div key={i} className="rounded border bg-background p-2 grid grid-cols-5 gap-x-4 gap-y-1">
            <Field label="Related" value={(sp.TicketRelation?.long_name ?? "")} />
            <Field label="Split Percentage" value={`${new Decimal(sp.split_percentage ?? 1).times(100)}%`} />
            <NumericField label="Split" value={sp.scale_quantity_lb} uom={e.dest_uom} conversion_factor={e.commodity_conversion_factor} />
            <NumericField label="Applied" value={sp.applied_quantity_lb} uom={e.dest_uom} conversion_factor={e.commodity_conversion_factor} />
            <NumericField label="Shrink" value={sp.shrink_quantity_lb} uom={e.dest_uom} conversion_factor={e.commodity_conversion_factor} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Entry block ────────────────────────────────────────────────────
export function EntryBlock(
  { entry, onContractClick }:
  { entry: ExpandedTransformedEntry; onContractClick?: (item: { contract_nbr: number; contract_id: string; settlement_nbr?: number; advance_nbr?: number }) => void }
) {
  const netWeightRaw =
    entry.scale_ticket_gross_weight !== null && entry.scale_ticket_tare_weight !== null
      ? String(Number(entry.scale_ticket_gross_weight) - Number(entry.scale_ticket_tare_weight))
      : null;

  return (
    <div className="rounded-lg border bg-muted/50 p-3 space-y-2">
      <div className="grid grid-cols-5 gap-x-4 gap-y-1">
        <Field label="Origin" value={entry.ShipFrom?.long_name ?? fmt(entry.ship_from_id)} />
        <Field label="Destination" value={entry.ShipTo?.long_name ?? fmt(entry.ship_to_id)} />
        <Field label="Vehicle" value={fmt(entry.vehicle_id)} />
        <Field label="Load" value={fmt(entry.reference_code)} />
        <Field label="Truck" value={fmt(entry.train_barge)} />
      </div>

      {/* Weights row */}
      <div className="grid grid-cols-4 gap-x-4 gap-y-1">
        <NumericField label="Gross" value={entry.scale_ticket_gross_weight} uom="lbs" />
        <NumericField label="Tare" value={entry.scale_ticket_tare_weight} uom="lbs" />
        <NumericField label="Net" value={netWeightRaw} uom="lbs" />
        <NumericField label="Scale" value={entry.scale_quantity_lb} uom={entry.dest_uom} conversion_factor={entry.commodity_conversion_factor} />
      </div>

      {/* Base Grades */}
      <BaseGradesBlock grades={entry.grades} />

      {/* Splits */}
      <SplitsBlock splits={entry.splits} e={entry}/>

      {/* Settlements */}
      {entry.settlements.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold">
            Settlements ({entry.settlements.length})
          </h4>
          <div className="grid grid-cols-1 gap-1">
            {entry.settlements.map((s) => (
              <SettlementBlock
                key={s.settlement_nbr}
                s={s}
                e={entry}
                onClick={s.contract_nbr && onContractClick ? () => onContractClick({ contract_nbr: s.contract_nbr, contract_id: s.contract?.contract_id?.toString()!, settlement_nbr: s.settlement_nbr }) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Advances */}
      {entry.advances.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-semibold">
            Advances ({entry.advances.length})
          </h4>
          <div className="grid grid-cols-1 gap-1">
            {entry.advances.map((a) => (
              <AdvanceBlock
                key={a.advance_nbr}
                a={a}
                e={entry}
                onClick={a.contract_nbr && onContractClick ? () => onContractClick({ contract_nbr: a.contract_nbr, contract_id: a.contract?.contract_id?.toString()!, advance_nbr: a.advance_nbr }) : undefined}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
